import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { OmdProductPanel } from '../components/OmdProductPanel';
import type { Material, MaterialFolder, SupplierCatalog } from '../types/material';
import { createMaterial, deleteMaterial, loadMaterialsFromApi, saveMaterialEdit } from '../utils/materialApi';
import {
  calcMaterialVk,
  materialToEditDraft,
  normalizeMaterialVat,
  validateMaterialEditDraft,
  validateMaterialSurcharge,
} from '../utils/materialForm';
import {
  materialMatchesOmdSelection,
  resolveArticleNumber,
  stubMaterialFromOmdSelection,
} from '../components/omd-panel/omdPanelHelpers';
import { parseGermanNumber } from '../../../utils/parseGermanNumber';
import { parseStammdatenOmdSearch, setStammdatenOmdSearch } from '../../../utils/stammdatenRoutes';
import { MaterialErrorBanner } from '../components/materialien/MaterialErrorBanner';
import { MaterialCreateModal } from '../components/materialien/MaterialCreateModal';
import { MaterialienListPanel } from '../components/materialien/MaterialienListPanel';
import { MaterialMobileEditOverlay } from '../components/materialien/MaterialMobileEditOverlay';
import { useModuleAccess } from '../../../hooks/useModuleAccess';

export type MaterialienSectionProps = {
  selectedFolderId: string | null;
  selectedSupplierId: string | null;
  onClearFilters: () => void;
  ordnerFolders: MaterialFolder[];
  supplierCatalogs: SupplierCatalog[];
  onFoldersRefresh: () => void | Promise<void>;
  onTotalCountChange: (count: number) => void;
  mobileCreateOpen: boolean;
  onCloseMobileCreate: () => void;
};

const MATERIAL_SEARCH_MIN_LENGTH = 2;
const MATERIAL_SEARCH_DEBOUNCE_MS = 400;
/** Ohne OMD-Modul gilt kein Katalogartikel als OMD-Artikel (kein Produktpanel). */
const NO_OMD_SLUGS: ReadonlySet<string> = new Set();

export function MaterialienSection({
  selectedFolderId,
  selectedSupplierId,
  onClearFilters,
  ordnerFolders,
  supplierCatalogs,
  onFoldersRefresh,
  onTotalCountChange,
  mobileCreateOpen,
  onCloseMobileCreate,
}: MaterialienSectionProps) {
  const { loaded: modulesLoaded, isEnabled } = useModuleAccess();
  const omdAvailable = modulesLoaded && isEnabled('omd');
  const [materials, setMaterials] = React.useState<Material[]>([]);
  const [searchResultsCount, setSearchResultsCount] = React.useState(0);
  const [searchPage, setSearchPage] = React.useState(1);
  const [searchTotalPages, setSearchTotalPages] = React.useState(1);
  const [searchMode, setSearchMode] = React.useState<'semantic' | 'keyword' | 'exact' | null>(null);
  const [searchPreference, setSearchPreference] = React.useState<'semantic' | 'keyword'>('keyword');
  const [appliedSearchPreference, setAppliedSearchPreference] = React.useState<'semantic' | 'keyword'>('keyword');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [name, setName] = React.useState('');
  const [brand, setBrand] = React.useState('');
  const [supplier, setSupplier] = React.useState('');
  const [gtin, setGtin] = React.useState('');
  const [unit, setUnit] = React.useState('');
  const [ekPrice, setEkPrice] = React.useState('');
  const [surchargePercent, setSurchargePercent] = React.useState('');
  const [vatPercent, setVatPercent] = React.useState('19');
  const [description, setDescription] = React.useState('');
  const [minutes, setMinutes] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState('');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchGeneration, setSearchGeneration] = React.useState(0);
  const [searchSubmitting, setSearchSubmitting] = React.useState(false);
  const [searchHint, setSearchHint] = React.useState<string | null>(null);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editDraft, setEditDraft] = React.useState<Partial<Material>>({});
  const [createSectionOpen, setCreateSectionOpen] = React.useState(false);
  const [mobileEditMaterial, setMobileEditMaterial] = React.useState<Material | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [omdEnabledSlugs, setOmdEnabledSlugs] = React.useState<ReadonlySet<string>>(() => new Set(['g-c']));
  const materialsListRef = React.useRef<HTMLDivElement | null>(null);
  const prevFiltersRef = React.useRef<{ folder: string | null; supplier: string | null } | null>(null);
  const loadAbortRef = React.useRef<AbortController | null>(null);

  const previewVk = React.useMemo(() => calcMaterialVk(ekPrice, surchargePercent), [ekPrice, surchargePercent]);

  const omdFromUrl = React.useMemo(() => parseStammdatenOmdSearch(searchParams), [searchParams]);

  const openOmdPanel = React.useCallback(
    (m: Material) => {
      const supplierSlug = String(m.supplier_slug ?? '').trim().toLowerCase();
      const article = resolveArticleNumber(m);
      if (!supplierSlug || !article) return;
      setSearchParams(
        (prev) => setStammdatenOmdSearch(new URLSearchParams(prev), { supplierSlug, article }),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const closeOmdPanel = React.useCallback(() => {
    setSearchParams((prev) => setStammdatenOmdSearch(new URLSearchParams(prev), null), { replace: true });
  }, [setSearchParams]);

  const omdPanelMaterial = React.useMemo(() => {
    if (!omdFromUrl) return null;
    return materials.find((m) => materialMatchesOmdSelection(m, omdFromUrl)) ?? stubMaterialFromOmdSelection(omdFromUrl);
  }, [omdFromUrl, materials]);

  const omdPanelOpen = Boolean(omdFromUrl);
  const omdPanelEnabledSlugs = React.useMemo(() => {
    if (!omdFromUrl || omdEnabledSlugs.has(omdFromUrl.supplierSlug)) return omdEnabledSlugs;
    const next = new Set(omdEnabledSlugs);
    next.add(omdFromUrl.supplierSlug);
    return next;
  }, [omdFromUrl, omdEnabledSlugs]);

  React.useEffect(() => {
    if (!omdAvailable) return;
    let cancelled = false;
    fetch('/api/settings/omd', { credentials: 'include' })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (cancelled || !ok) return;
        const list = Array.isArray(d?.suppliers) ? d.suppliers : [];
        const slugs = new Set<string>(
          list
            .map((s: { slug?: string }) => String(s.slug ?? '').trim().toLowerCase())
            .filter(Boolean),
        );
        if (slugs.size > 0) setOmdEnabledSlugs(slugs);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [omdAvailable]);

  const loadMaterials = React.useCallback(
    (
      folderId: string | null = selectedFolderId,
      query: string = searchQuery,
      page = searchPage,
      supplierId: string | null = selectedSupplierId,
      preferredMode: 'semantic' | 'keyword' = appliedSearchPreference
    ) => {
      loadAbortRef.current?.abort();
      const controller = new AbortController();
      loadAbortRef.current = controller;
      setLoading(true);
      loadMaterialsFromApi(folderId, query, page, supplierId, preferredMode, controller.signal)
        .then((result) => {
          if (controller.signal.aborted) return;
          setMaterials(result.rows);
          setSearchResultsCount(result.searchResultsCount);
          setSearchTotalPages(result.searchTotalPages);
          setSearchMode(result.searchMode);
          setSearchPage(result.searchPage);
          if (result.totalMaterialsCount != null) onTotalCountChange(result.totalMaterialsCount);
        })
        .catch((err) => {
          if (err instanceof DOMException && err.name === 'AbortError') return;
          setError(err instanceof Error ? err.message : 'Materialien konnten nicht geladen werden.');
        })
        .finally(() => {
          if (loadAbortRef.current !== controller) return;
          loadAbortRef.current = null;
          setLoading(false);
          setSearchSubmitting(false);
        });
    },
    [selectedFolderId, selectedSupplierId, searchQuery, searchPage, appliedSearchPreference, onTotalCountChange]
  );

  const triggerSearch = React.useCallback(() => {
    const trimmed = searchInput.trim();
    if (!trimmed) {
      setSearchHint('Bitte zuerst einen Suchbegriff eingeben.');
      return;
    }
    setSearchHint(null);
    setSearchSubmitting(true);
    setSearchPage(1);
    setSearchTotalPages(1);
    setSearchMode(null);
    setSearchQuery(trimmed);
    setAppliedSearchPreference(searchPreference);
    setSearchGeneration((g) => g + 1);
  }, [searchInput, searchPreference]);

  const clearMaterialsSearch = React.useCallback(() => {
    setSearchQuery('');
    setSearchInput('');
    setSearchPage(1);
    setSearchTotalPages(1);
    setSearchMode(null);
    setSearchHint(null);
    setSearchSubmitting(false);
  }, []);

  const cancelSearch = React.useCallback(() => {
    loadAbortRef.current?.abort();
    loadAbortRef.current = null;
    setLoading(false);
    setSearchSubmitting(false);
  }, []);

  React.useEffect(() => () => loadAbortRef.current?.abort(), []);

  // Search as you type (no button): debounce, and clearing the field returns to the list.
  React.useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === searchQuery && searchPreference === appliedSearchPreference) return;
    if (!trimmed) {
      if (searchQuery) clearMaterialsSearch();
      return;
    }
    if (trimmed.length < MATERIAL_SEARCH_MIN_LENGTH) return;
    const t = window.setTimeout(triggerSearch, MATERIAL_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [searchInput, searchQuery, searchPreference, appliedSearchPreference, triggerSearch, clearMaterialsSearch]);

  const previousFilterKeyRef = React.useRef<string>('');
  const enabledSupplierKey = React.useMemo(
    () =>
      supplierCatalogs
        .map((s) => s.id)
        .sort()
        .join(','),
    [supplierCatalogs]
  );

  React.useEffect(() => {
    const filtersChanged =
      prevFiltersRef.current != null &&
      (prevFiltersRef.current.folder !== selectedFolderId ||
        prevFiltersRef.current.supplier !== selectedSupplierId);

    if (prevFiltersRef.current == null) {
      prevFiltersRef.current = { folder: selectedFolderId, supplier: selectedSupplierId };
    } else if (filtersChanged) {
      prevFiltersRef.current = { folder: selectedFolderId, supplier: selectedSupplierId };
    }

    const query = filtersChanged ? '' : searchQuery;
    const page = filtersChanged ? 1 : searchPage;
    if (filtersChanged && searchQuery) clearMaterialsSearch();

    loadMaterials(selectedFolderId, query, page, selectedSupplierId, appliedSearchPreference);
  }, [
    loadMaterials,
    selectedFolderId,
    selectedSupplierId,
    searchQuery,
    searchPage,
    searchGeneration,
    appliedSearchPreference,
    clearMaterialsSearch,
    enabledSupplierKey,
  ]);

  React.useEffect(() => {
    const filterKey = `${selectedFolderId ?? ''}::${selectedSupplierId ?? ''}`;
    const prev = previousFilterKeyRef.current;
    previousFilterKeyRef.current = filterKey;
    if (prev === filterKey) return;
    if (selectedFolderId == null && selectedSupplierId == null) return;
    setCreateSectionOpen(false);
    requestAnimationFrame(() => {
      materialsListRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [selectedFolderId, selectedSupplierId]);

  const resetCreateFields = React.useCallback(() => {
    setName('');
    setBrand('');
    setSupplier('');
    setGtin('');
    setUnit('');
    setEkPrice('');
    setSurchargePercent('');
    setVatPercent('19');
    setDescription('');
    setMinutes('');
  }, []);

  const handleCreate = (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    const fail = (message: string) => {
      setError(message);
      return Promise.reject(new Error(message));
    };
    if (!name.trim()) return fail('Name ist erforderlich.');
    if (!unit.trim()) return fail('Einheit ist erforderlich.');
    if (!ekPrice.trim()) return fail('Einkaufspreis (EK) ist erforderlich.');
    if (!surchargePercent.trim()) return fail('Zuschlag ist erforderlich.');
    const sur = parseGermanNumber(surchargePercent);
    if (!validateMaterialSurcharge(sur)) return fail('Der Zuschlag darf nicht negativ sein.');
    const vatN = Number(vatPercent);
    if (vatN !== 0 && vatN !== 7 && vatN !== 19) {
      return fail('MwSt. ist erforderlich (0 %, 7 % oder 19 %).');
    }
    setSaving(true);
    setError(null);
    const ordnerIds = new Set(ordnerFolders.map((f) => f.id));
    return createMaterial({
      name,
      brand,
      supplier,
      gtin,
      unit,
      ekPrice,
      surchargePercent,
      vatPercent,
      description,
      minutes,
      folderId: selectedFolderId,
      ordnerFolderIds: ordnerIds,
    })
      .then(() => {
        void onFoldersRefresh();
        resetCreateFields();
        setCreateSectionOpen(false);
        onCloseMobileCreate();
        loadMaterials();
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Material konnte nicht angelegt werden.');
        throw err;
      })
      .finally(() => setSaving(false));
  };

  const saveMaterialEditRow = React.useCallback(
    (m: Material, draft: Partial<Material>) => {
      const validationError = validateMaterialEditDraft(draft, m);
      if (validationError) {
        setError(validationError);
        return;
      }
      const ekVal = draft.net_price ?? m.net_price;
      const sur = Number(draft.surcharge_percent ?? 0);
      const vat = normalizeMaterialVat(draft.vat_percent);
      setSaving(true);
      setError(null);
      saveMaterialEdit(m, draft, {
        ekVal: Number(ekVal),
        surcharge: sur,
        vatPercent: vat,
      })
        .then((updated) => {
          setMaterials((prev) => prev.map((row) => (row.id === m.id ? updated : row)));
          void onFoldersRefresh();
          setEditingId(null);
          setEditDraft({});
          setMobileEditMaterial(null);
        })
        .catch((err) => setError(err instanceof Error ? err.message : 'Material konnte nicht gespeichert werden.'))
        .finally(() => setSaving(false));
    },
    [onFoldersRefresh]
  );

  const deleteMaterialRow = React.useCallback(
    (m: Material) => {
      if (m.kind === 'shared') return;
      setSaving(true);
      deleteMaterial(m.id)
        .then(() => {
          void onFoldersRefresh();
          setMobileEditMaterial(null);
          loadMaterials();
        })
        .catch((err) => setError(err instanceof Error ? err.message : 'Material konnte nicht gelöscht werden.'))
        .finally(() => setSaving(false));
    },
    [onFoldersRefresh, loadMaterials]
  );

  const materialsEmptyMessage = searchQuery.trim()
    ? `Keine Treffer für „${searchQuery.trim()}“.`
    : 'Noch keine Materialien erfasst.';

  return (
    <>
      {error && !createSectionOpen && !mobileCreateOpen && (
        <div className="bg-app-surface px-4 pt-3">
          <MaterialErrorBanner error={error} onDismiss={() => setError(null)} />
        </div>
      )}

      <MaterialienListPanel
        listRef={materialsListRef}
        ordnerFolders={ordnerFolders}
        supplierCatalogs={supplierCatalogs}
        selectedFolderId={selectedFolderId}
        selectedSupplierId={selectedSupplierId}
        onClearFilters={onClearFilters}
        materials={materials}
        loading={loading}
        emptyMessage={materialsEmptyMessage}
        searchHint={searchHint}
        searchResultsCount={searchResultsCount}
        searchMode={searchMode}
        searchPage={searchPage}
        searchTotalPages={searchTotalPages}
        searchPreference={searchPreference}
        onSearchPreferenceChange={setSearchPreference}
        searchInput={searchInput}
        onSearchInputChange={setSearchInput}
        onSearchHintClear={() => setSearchHint(null)}
        onTriggerSearch={triggerSearch}
        onCancelSearch={cancelSearch}
        searchSubmitting={searchSubmitting}
        editingId={editingId}
        editDraft={editDraft}
        setEditDraft={setEditDraft}
        saving={saving}
        onStartEdit={(m) => {
          setEditingId(m.id);
          setEditDraft(materialToEditDraft(m));
        }}
        onCancelEdit={() => {
          setEditingId(null);
          setEditDraft({});
        }}
        onSaveEdit={saveMaterialEditRow}
        onDelete={deleteMaterialRow}
        onOpenOmdPanel={openOmdPanel}
        onEditMaterialMobile={(m) => {
          setEditDraft(materialToEditDraft(m));
          setMobileEditMaterial(m);
        }}
        onSearchPageChange={setSearchPage}
        omdEnabledSlugs={omdAvailable ? omdEnabledSlugs : NO_OMD_SLUGS}
        onCreateClick={() => {
          setError(null);
          setCreateSectionOpen(true);
        }}
      />

      {mobileEditMaterial && (
        <MaterialMobileEditOverlay
          material={mobileEditMaterial}
          editDraft={editDraft}
          setEditDraft={setEditDraft}
          saving={saving}
          onClose={() => {
            setMobileEditMaterial(null);
            setEditDraft({});
          }}
          onSave={saveMaterialEditRow}
          onDelete={deleteMaterialRow}
        />
      )}

      <MaterialCreateModal
        open={createSectionOpen || mobileCreateOpen}
        onClose={() => {
          setCreateSectionOpen(false);
          onCloseMobileCreate();
        }}
        saving={saving}
        previewVk={previewVk}
        error={createSectionOpen || mobileCreateOpen ? error : null}
        name={name}
        brand={brand}
        supplier={supplier}
        gtin={gtin}
        unit={unit}
        ekPrice={ekPrice}
        surchargePercent={surchargePercent}
        vatPercent={vatPercent}
        description={description}
        minutes={minutes}
        onNameChange={setName}
        onBrandChange={setBrand}
        onSupplierChange={setSupplier}
        onGtinChange={setGtin}
        onUnitChange={setUnit}
        onEkPriceChange={setEkPrice}
        onSurchargePercentChange={setSurchargePercent}
        onVatPercentChange={setVatPercent}
        onDescriptionChange={setDescription}
        onMinutesChange={setMinutes}
        onSubmit={(e) => {
          void handleCreate(e).catch(() => undefined);
        }}
      />

      {omdAvailable && (
        <OmdProductPanel
          material={omdPanelMaterial}
          open={omdPanelOpen}
          onClose={closeOmdPanel}
          omdEnabledSlugs={omdPanelEnabledSlugs}
        />
      )}
    </>
  );
}
